import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";
import { getSiteOrigin } from "../siteOrigin";
import { ArrowLeft } from "lucide-react";

export default function BlogPostPage() {
  const { slug } = useParams();
  const { lang, t } = useI18n();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <main className="pt-40 pb-32 min-h-screen bg-[#050505] text-ink-300 text-center" data-testid="page-blog-post-404">404</main>;
  if (!post) return <main className="pt-40 pb-32 min-h-screen bg-[#050505]" />;

  const body = lang === "fr" ? post.body_fr : post.body;
  const title = lang === "fr" ? post.title_fr : post.title;
  const excerpt = lang === "fr" ? post.excerpt_fr : post.excerpt;

  const SITE = getSiteOrigin();
  const absCover = post.cover?.startsWith("http") ? post.cover : `${SITE}${post.cover}`;
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: lang === "fr" ? "Accueil" : "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: lang === "fr" ? "Journal" : "Journal", item: `${SITE}/journal` },
      { "@type": "ListItem", position: 3, name: title, item: `${SITE}/journal/${slug}` },
    ],
  };
  const schema = [{
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${SITE}/journal/${slug}#post`,
    headline: title,
    image: absCover,
    datePublished: post.date,
    dateModified: post.updated_at || post.date,
    inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
    mainEntityOfPage: `${SITE}/journal/${slug}`,
    author: {
      "@type": "Organization",
      "@id": `${SITE}/#studio`,
      name: "Phaneroo Prod",
      url: `${SITE}/studio`,
      address: {
        "@type": "PostalAddress",
        addressLocality: "Moncton",
        addressRegion: "NB",
        addressCountry: "CA",
      },
    },
    publisher: {
      "@type": "Organization",
      "@id": `${SITE}/#studio`,
      name: "Phaneroo Prod",
      logo: { "@type": "ImageObject", url: `${SITE}/generated/founder_cinema_camera.jpg` },
    },
    description: excerpt,
    wordCount: (lang === "fr" ? post.body_fr : post.body).split(/\s+/).length,
    timeRequired: `PT${post.reading_time}M`,
    keywords: lang === "fr"
      ? "production vidéo Moncton, photographie Canada Atlantique, Phaneroo Prod, studio cinématique Nouveau-Brunswick"
      : "video production Moncton, photography Atlantic Canada, Phaneroo Prod, cinematic studio New Brunswick",
  }, breadcrumb];

  return (
    <main className="pt-40 lg:pt-48 pb-32 bg-[#050505] min-h-screen" data-testid="page-blog-post">
      <SEO
        title={title}
        description={excerpt}
        path={`/journal/${slug}`}
        image={post.cover}
        lang={lang}
        schema={schema}
      />
      <article className="mx-auto max-w-3xl px-6">
        <Link to="/journal" className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-100 text-[11px] tracking-[0.22em] uppercase mb-10"><ArrowLeft size={14} /> {t.blog.eyebrow}</Link>
        <Reveal>
          <div className="text-[11px] tracking-[0.22em] uppercase text-ink-500">{new Date(post.date).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { day: "2-digit", month: "long", year: "numeric" })} · {post.reading_time} {t.blog.min}</div>
          <h1 className="mt-4 font-serif-editorial text-4xl sm:text-5xl lg:text-6xl text-ink-100 tracking-tight leading-tight">
            {title}
          </h1>
        </Reveal>
        <div className="mt-10 aspect-[16/9] overflow-hidden">
          <img src={post.cover} alt={title} className="w-full h-full object-cover" />
        </div>
        <div className="mt-12 prose prose-invert prose-lg max-w-none prose-headings:font-serif-editorial prose-headings:text-ink-100 prose-headings:tracking-tight prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-2xl prose-h3:mt-10 prose-h3:mb-3 prose-p:text-ink-300 prose-p:text-lg prose-p:leading-relaxed prose-a:text-rouge prose-a:no-underline hover:prose-a:underline prose-strong:text-ink-100 prose-li:text-ink-300 prose-blockquote:border-l-rouge prose-blockquote:text-ink-100 prose-hr:border-hair prose-code:text-rouge prose-code:bg-surface prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
        </div>
      </article>
    </main>
  );
}
